import AccountBoxIcon from '@mui/icons-material/AccountBox';
import {
	Autocomplete,
	CircularProgress,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import {
	Controller,
	FieldValues,
	Path,
	UseControllerProps,
	useFormContext,
} from 'react-hook-form';
import { SelectFieldConfiguration } from '../../../api/ConfigApi';
import SearchApi, { Result } from '../../../api/SearchApi';
import { useAuthContext } from '../../../context/auth';
import { ConfigData, useConfigContext } from '../../../context/config';
import FieldLabel from '../../fields/FieldLabel';
import useDebounce, { getTemplateResult } from '../../search/util';
import { FORM_NAME, PAYMENT_SPECIAL_FIELDS } from '../data/constants';

interface PolicyholderResult {
	title1: string;
	title2: string;
	subtitle1: string;
	locator: string;
}

type FieldProps = Omit<SelectFieldConfiguration, 'name'>;
interface Props<T> extends FieldProps, UseControllerProps<T> {
	policyholderKey?: string;
}

const PolicyholderLookupField = <T extends FieldValues>(props: Props<T>) => {
	const authData = useAuthContext();
	const configData = useConfigContext();
	const { watch } = useFormContext();
	const watchFor =
		props.policyholderKey ||
		`${FORM_NAME.payment}.${PAYMENT_SPECIAL_FIELDS.policyholderLocator}`;
	const prefilledValue = watch(watchFor);
	const [inputValue, setInputValue] = React.useState('');
	const [value, setValue] = React.useState<PolicyholderResult | null>(null);
	const debouncedSearchValue = useDebounce(inputValue, 500);

	const authToken = authData.auth?.authorizationToken;
	const { name, control, disabled } = props;

	const { data: prefilledSearchResults } = useQuery({
		queryFn: async () => {
			const searchResults = await SearchApi.search(authToken, prefilledValue);
			const searchData = searchResults.data.results;
			const policyholderResults =
				searchData &&
				searchData.filter((r) => r.entityType === 'entity.policyholder');
			return policyholderResults;
		},
		queryKey: ['policyholders', prefilledValue, authToken],
		enabled: !!prefilledValue,
		refetchIntervalInBackground: false,
	});

	useEffect(() => {
		if (prefilledValue && prefilledSearchResults) {
			const policyholderResult = getPolicyholderData(
				prefilledSearchResults[0],
				configData,
			);
			setInputValue(policyholderResult.locator);
			setValue(policyholderResult);
		}
	}, [prefilledValue, prefilledSearchResults, configData]);

	const { data: policyholders, isLoading: isPolicyholderSearchLoading } =
		useQuery({
			queryFn: async () => {
				const searchResults = await SearchApi.search(
					authToken,
					debouncedSearchValue,
				);
				const searchData = searchResults.data.results;
				const policyholderResults =
					searchData &&
					searchData.filter((r) => r.entityType === 'entity.policyholder');
				return policyholderResults;
			},
			queryKey: ['policyholders', debouncedSearchValue, authToken],
		});

	const getPolicyholderData = (
		searchResult: Result,
		configData: ConfigData,
	): PolicyholderResult => {
		const policyholderCardDefinition =
			configData.policyholderCardConfig?.workflows?.find(
				(w: any) => w.message === 'policyHolderSearch.json',
			).value;

		if (!policyholderCardDefinition) {
			return {
				title1: '',
				title2: '',
				subtitle1: '',
				locator: '',
			};
		}

		const title1 = getTemplateResult(
			policyholderCardDefinition.title,
			configData.ontologyData.items,
			searchResult.entity.values,
		);
		const title2 = getTemplateResult(
			policyholderCardDefinition.subtitle,
			configData.ontologyData.items,
			searchResult.entity.values,
		);
		const subtitle1 = getTemplateResult(
			policyholderCardDefinition.tag,
			configData.ontologyData.items,
			searchResult.entity.values,
		);

		const value: PolicyholderResult = {
			title1,
			title2,
			subtitle1,
			locator: searchResult.entity.id,
		};

		return value;
	};

	const policyholderOptions = policyholders
		? policyholders.map((p) => getPolicyholderData(p, configData))
		: [];

	return (
		<Controller
			control={control}
			name={name as Path<T>}
			render={({
				field: { onChange, onBlur, onChange: onFieldValueChange },
				fieldState: { error },
			}) => (
				<FormControl error={!!error} sx={{ width: '100%' }}>
					<FieldLabel title='Policyholder' htmlFor='policyholder' />
					<Autocomplete
						disabled={disabled}
						loading={isPolicyholderSearchLoading}
						getOptionLabel={(option) =>
							typeof option === 'string' ? option : option.title1
						}
						filterOptions={(x) => x}
						options={policyholderOptions}
						autoComplete
						includeInputInList
						value={value}
						isOptionEqualToValue={(option, value) =>
							option.locator === value.locator
						}
						onChange={(event: any, newValue: PolicyholderResult | null) => {
							setValue(newValue);
							onChange(newValue ? newValue.locator : '');
						}}
						onInputChange={(event, newInputValue, reason) => {
							if (reason === 'input' || reason === 'clear') {
								setInputValue(newInputValue);
								onFieldValueChange('');
							}
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								error={!!error}
								helperText={error?.message}
								disabled={disabled}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{isPolicyholderSearchLoading ? (
												<CircularProgress color='inherit' size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
								onBlur={(e) => {
									onBlur();
								}}
								sx={{
									'& .MuiInput-input:focus': {
										backgroundColor: 'transparent',
									},
								}}
							/>
						)}
						renderOption={(props, option) => {
							return (
								<li {...props}>
									<Grid container alignItems='center'>
										<Grid item sx={{ display: 'flex', width: 44 }}>
											<AccountBoxIcon sx={{ color: 'text.secondary' }} />
										</Grid>
										<Grid
											item
											sx={{
												width: 'calc(100% - 44px)',
												wordWrap: 'break-word',
											}}
										>
											<Typography variant='body1' color='text.primary'>
												{option.title1}
											</Typography>
											<Typography variant='body2' color='text.secondary'>
												{option.title2}
											</Typography>
										</Grid>
									</Grid>
								</li>
							);
						}}
					/>
				</FormControl>
			)}
		/>
	);
};

export default PolicyholderLookupField;
